import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import { formatDateTime } from '@formosa/shared/utils/formatDateTime';
import Pagination from '../../components/Pagination';
import {
  MEMBER_ROLE_OPTIONS,
  buildWithdrawAddressPageParams,
  formatOptionalId,
  formatPaymentCurrencyType,
  formatRoleId,
} from './withdrawAddressConstants';
import WithdrawAddressEditModal from './WithdrawAddressEditModal';

const PAGE_SIZE = 20;

const EMPTY_FILTERS = {
  roleId: '',
  topInviterId: '',
  inviterId: '',
  memberId: '',
  channelType: '',
  currencyName: '',
  withdrawAddress: '',
};

const TABLE_COLUMNS = [
  '頂級上級用戶ID',
  '直屬上級用戶ID',
  '用戶ID',
  '帳號類型',
  '通道代碼',
  '幣種',
  '通道名稱',
  '地址類型',
  '戶名',
  '開戶行',
  '提現地址',
  '綁定時間',
  '操作',
];

export default function MemberWithdrawAddressListPage() {
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(true);
  const [exporting, setExporting] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const params = buildWithdrawAddressPageParams(pageNo, nextFilters, PAGE_SIZE);
      const data = await adminApi.withdrawAddressPage(params);
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
      setTotal(0);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setError('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_FILTERS };
    setFilters(reset);
    setError('');
    loadRecords(1, reset);
  }

  async function handleExport(exportCurrentPageOnly = false) {
    setExporting(true);
    setError('');
    try {
      const params = {
        ...buildWithdrawAddressPageParams(page, filters, PAGE_SIZE),
        fileName: 'member_withdraw_address',
        exportCurrentPageOnly,
      };
      await adminApi.exportWithdrawAddressCsvZip(params);
    } catch (err) {
      setError(err.message || '匯出失敗');
    } finally {
      setExporting(false);
    }
  }

  async function handleUpdateAddress(payload) {
    setSubmitting(true);
    setError('');
    try {
      await adminApi.updateWithdrawAddress(payload);
      setEditRow(null);
      await loadRecords(page);
    } catch (err) {
      setError(err.message || '修改失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>提現地址列表</h2>
        <div className="section-header__actions">
          <button
            type="button"
            className="btn btn-secondary"
            disabled={exporting || loading}
            onClick={() => handleExport(false)}
          >
            {exporting ? '匯出中…' : '匯出全部'}
          </button>
          <button
            type="button"
            className="btn btn-secondary"
            disabled={exporting || loading}
            onClick={() => handleExport(true)}
          >
            匯出本頁
          </button>
        </div>
      </div>

      <form className="filter-bar filter-bar--grid" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="waRoleId">帳號類型</label>
          <select
            id="waRoleId"
            value={filters.roleId}
            onChange={(e) => updateFilter('roleId', e.target.value)}
          >
            {MEMBER_ROLE_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="waTopInviterId">頂級上級用戶ID</label>
          <input
            id="waTopInviterId"
            value={filters.topInviterId}
            onChange={(e) => updateFilter('topInviterId', e.target.value)}
            placeholder="精確"
          />
        </div>
        <div className="form-field">
          <label htmlFor="waInviterId">直屬上級用戶ID</label>
          <input
            id="waInviterId"
            value={filters.inviterId}
            onChange={(e) => updateFilter('inviterId', e.target.value)}
            placeholder="前綴"
          />
        </div>
        <div className="form-field">
          <label htmlFor="waMemberId">用戶ID</label>
          <input
            id="waMemberId"
            value={filters.memberId}
            onChange={(e) => updateFilter('memberId', e.target.value)}
            placeholder="前綴"
          />
        </div>
        <div className="form-field">
          <label htmlFor="waChannelType">通道代碼</label>
          <input
            id="waChannelType"
            value={filters.channelType}
            onChange={(e) => updateFilter('channelType', e.target.value)}
            placeholder="前綴"
          />
        </div>
        <div className="form-field">
          <label htmlFor="waCurrencyName">幣種</label>
          <input
            id="waCurrencyName"
            value={filters.currencyName}
            onChange={(e) => updateFilter('currencyName', e.target.value)}
            placeholder="如 USDT"
          />
        </div>
        <div className="form-field">
          <label htmlFor="waWithdrawAddress">提現地址</label>
          <input
            id="waWithdrawAddress"
            value={filters.withdrawAddress}
            onChange={(e) => updateFilter('withdrawAddress', e.target.value)}
            placeholder="精確比對"
          />
        </div>
        <div className="filter-bar__actions">
          <button type="submit" className="btn btn-primary">
            搜尋
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
        </div>
      </form>

      {!loading && <p className="page-desc">共 {total} 筆</p>}
      {loading && <p>載入中…</p>}
      {error && <div className="alert alert-error">{error}</div>}

      <div className="table-scroll">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {!loading && records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="empty-cell">
                  尚無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{formatOptionalId(row.topInviterId)}</td>
                  <td>{formatOptionalId(row.inviterId)}</td>
                  <td>{formatOptionalId(row.memberId)}</td>
                  <td>{formatRoleId(row.roleId)}</td>
                  <td>{row.channelType || '—'}</td>
                  <td>{row.currencyName || '—'}</td>
                  <td>{row.channelName || '—'}</td>
                  <td>{formatPaymentCurrencyType(row.paymentCurrencyType)}</td>
                  <td>{row.walletName || '—'}</td>
                  <td>{row.bankName || '—'}</td>
                  <td className="mono-cell">{row.withdrawAddress || '—'}</td>
                  <td>{formatDateTime(row.createTime)}</td>
                  <td>
                    <button
                      type="button"
                      className="btn btn-success btn-sm"
                      onClick={() => setEditRow(row)}
                    >
                      修改
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onPageChange={(p) => loadRecords(p)} />

      <WithdrawAddressEditModal
        open={Boolean(editRow)}
        row={editRow}
        onClose={() => setEditRow(null)}
        onSubmit={handleUpdateAddress}
        submitting={submitting}
      />
    </div>
  );
}
